/**
 * Onboarding Progress Hook
 *
 * Tracks user's onboarding progress through the main setup steps.
 * Determines if user is "new" and what step they should complete next.
 */

import { useEffect, useMemo } from 'react';
import { useProperties } from './useProperties';
import { useAllUnits } from './useUnits';
import { useBookings } from './useBookings';
import { useOnboardingStore } from '@/stores/onboardingStore';

// ============================================
// TYPES
// ============================================

export interface OnboardingStep {
  id: string;
  title: { hr: string; en: string };
  description: { hr: string; en: string };
  route: string;
  isCompleted: boolean;
  icon: string;
}

export interface UseOnboardingProgressReturn {
  /** All onboarding steps with completion status */
  steps: OnboardingStep[];
  /** The next step user should complete (or null if all done) */
  currentStep: OnboardingStep | null;
  /** Completion percentage (0-100) */
  progress: number;
  /** Number of completed steps */
  completedCount: number;
  /** Total number of steps */
  totalCount: number;
  /** Whether onboarding is complete (all main steps done) */
  isOnboardingComplete: boolean;
  /** Whether user is completely new (no properties) */
  isNewUser: boolean;
  /** Loading state */
  isLoading: boolean;
}

// ============================================
// HOOK
// ============================================

export function useOnboardingProgress(): UseOnboardingProgressReturn {
  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  const { data: units = [], isLoading: unitsLoading } = useAllUnits();
  const { data: bookings = [], isLoading: bookingsLoading } = useBookings();

  const isLoading = propertiesLoading || unitsLoading || bookingsLoading;

  // Calculate step completion
  const hasProperty = properties.length > 0;
  const hasUnit = units.length > 0;
  const hasBooking = bookings.length > 0;

  // Push to the central onboarding store whenever real DB counts change. The
  // store no-ops if nothing changed, so this is cheap.
  const setProgress = useOnboardingStore((s) => s.setProgress);
  useEffect(() => {
    if (isLoading) return;
    setProgress({ hasProperty, hasUnit, hasBooking });
  }, [hasProperty, hasUnit, hasBooking, isLoading, setProgress]);

  // Define steps
  const steps: OnboardingStep[] = useMemo(() => [
    {
      id: 'add_property',
      title: { hr: 'Dodaj objekt', en: 'Add property' },
      description: {
        hr: 'Kreirajte svoj prvi objekt (kuća, vila, zgrada)',
        en: 'Create your first property (house, villa, building)',
      },
      route: '/dashboard/properties/new',
      isCompleted: hasProperty,
      icon: '🏠',
    },
    {
      id: 'add_unit',
      title: { hr: 'Dodaj jedinicu', en: 'Add unit' },
      description: {
        hr: 'Dodajte apartman ili sobu u objekt',
        en: 'Add an apartment or room to the property',
      },
      route: hasProperty && properties[0]
        ? `/dashboard/properties/${properties[0].id}`
        : '/dashboard/properties',
      isCompleted: hasUnit,
      icon: '🛏️',
    },
    {
      id: 'create_booking',
      title: { hr: 'Kreiraj rezervaciju', en: 'Create booking' },
      description: {
        hr: 'Unesite prvog gosta i generirajte pristupni kod',
        en: 'Enter your first guest and generate an access code',
      },
      route: '/dashboard/bookings/new',
      isCompleted: hasBooking,
      icon: '📅',
    },
  ], [hasProperty, hasUnit, hasBooking, properties]);

  // Calculate progress
  const completedCount = steps.filter(s => s.isCompleted).length;
  const totalCount = steps.length;
  const progress = Math.round((completedCount / totalCount) * 100);

  // Find current (next uncompleted) step
  const currentStep = steps.find(s => !s.isCompleted) || null;

  // Onboarding is complete when all main steps are done
  const isOnboardingComplete = completedCount === totalCount;

  // User is "new" if they have no properties
  const isNewUser = !hasProperty && !isLoading;

  return {
    steps,
    currentStep,
    progress,
    completedCount,
    totalCount,
    isOnboardingComplete,
    isNewUser,
    isLoading,
  };
}

export default useOnboardingProgress;
